import React from 'react';
import {
  Shield,
  Activity,
  TrendingUp,
  Eye,
  Clock,
  Flag,
  RotateCcw,
  AlertTriangle,
  CheckCircle,
  MessageSquareText,
  ChevronRight,
} from 'lucide-react';
import { Device } from '../lib/supabase';
import { useSystemStatus } from '../hooks/useSystemStatus';
import { useFeatureFlag } from '../hooks/useFeatureFlags';
import { useLockAppStatus } from '../hooks/useLockAppStatus';
import { useLockMessages } from '../hooks/useLockMessages';

interface DashboardProps {
  onNavigateToDevices: (filter?: 'all' | 'locked' | 'unlocked') => void;
  onViewDevice: (device: Device) => void;
}

export function Dashboard({ onNavigateToDevices, onViewDevice }: DashboardProps) {
  const { status } = useSystemStatus();
  const { isEnabled: showLockRequests, loading: flagLoading } = useFeatureFlag('show_lock_requests', false);
  const { devicesWithStatus, summary, loading, error } = useLockAppStatus();
  const { messages: lockMessages } = useLockMessages();
  const [hoveredTemplate, setHoveredTemplate] = React.useState<{ name: string; count: number } | null>(null);

  const safePercent = (value: number, total: number) => {
    if (!total) return 0;
    return Math.round((value / total) * 100);
  };

  if (loading) {
    return (
      <div className="space-y-6 animate-fade-in premium-grid-bg rounded-3xl p-5 sm:p-7">
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-5">
          {[...Array(8)].map((_, i) => (
            <div
              key={i}
              className="glass-panel rounded-2xl p-6 border border-white/30 dark:border-slate-700/60 animate-pulse"
              style={{ animationDelay: `${i * 80}ms` }}
            >
              <div className="h-4 bg-slate-200/80 dark:bg-slate-700 rounded w-2/3 mb-4" />
              <div className="h-8 bg-slate-200/80 dark:bg-slate-700 rounded w-1/2 mb-4" />
              <div className="h-2 bg-slate-200/70 dark:bg-slate-700 rounded w-full" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="glass-panel border border-rose-300/60 dark:border-rose-900/50 rounded-2xl p-6">
        <div className="flex items-center space-x-3">
          <AlertTriangle className="h-6 w-6 text-rose-600 dark:text-rose-400" />
          <div>
            <h3 className="text-lg font-semibold text-rose-800 dark:text-rose-100">Error loading dashboard data</h3>
            <p className="text-rose-600 dark:text-rose-300">{error}</p>
          </div>
        </div>
      </div>
    );
  }

  const lockedDevices = devicesWithStatus.filter((device) => device.is_locked);
  const lockMessageTitleById = new Map(lockMessages.map((message) => [message.id, message.title]));
  const templateUsage = new Map<string, number>();

  lockedDevices.forEach((device) => {
    const templateTitle = (device.lock_message_id && lockMessageTitleById.get(device.lock_message_id)) || 'No template assigned';
    templateUsage.set(templateTitle, (templateUsage.get(templateTitle) || 0) + 1);
  });

  const templateBreakdown = Array.from(templateUsage.entries()).sort((left, right) => right[1] - left[1]);

  const templateBarGradients = [
    'from-indigo-400 to-blue-500',
    'from-cyan-400 to-teal-500',
    'from-violet-400 to-fuchsia-500',
    'from-blue-400 to-indigo-500',
    'from-sky-400 to-cyan-500',
    'from-teal-400 to-emerald-500',
    'from-purple-400 to-violet-500',
    'from-slate-400 to-slate-500'
  ];

  const truncateLabel = (value: string, maxLength = 24) => {
    if (value.length <= maxLength) return value;
    return `${value.slice(0, maxLength - 1)}…`;
  };

  const largestTemplate = templateBreakdown[0];
  const activeTemplate = hoveredTemplate || (largestTemplate
    ? { name: largestTemplate[0], count: largestTemplate[1] }
    : null);

  const stats = [
    {
      title: 'Fleet Overview',
      value: summary.totalDevices,
      icon: Shield,
      gradient: 'from-cyan-400 via-sky-500 to-blue-600',
      textColor: 'text-sky-700 dark:text-sky-300',
      onClick: () => onNavigateToDevices('all'),
      tone: 'bg-sky-500/10 border-sky-200/50 dark:border-sky-600/30'
    },
    {
      title: 'Reporting Coverage',
      value: summary.lockAppRun,
      icon: CheckCircle,
      gradient: 'from-emerald-400 via-emerald-500 to-teal-600',
      textColor: 'text-emerald-700 dark:text-emerald-300',
      onClick: () => onNavigateToDevices('all'),
      tone: 'bg-emerald-500/10 border-emerald-200/50 dark:border-emerald-600/30'
    },
    {
      title: 'Sync Integrity',
      value: summary.syncedDevices,
      icon: RotateCcw,
      gradient: 'from-violet-400 via-indigo-500 to-blue-600',
      textColor: 'text-indigo-700 dark:text-indigo-300',
      onClick: () => onNavigateToDevices('all'),
      tone: 'bg-indigo-500/10 border-indigo-200/50 dark:border-indigo-600/30'
    },
    {
      title: 'Locked by Template',
      value: summary.lockedInDatabase,
      icon: MessageSquareText,
      gradient: 'from-slate-500 via-indigo-500 to-cyan-500',
      textColor: 'text-indigo-700 dark:text-indigo-300',
      onClick: () => onNavigateToDevices('locked'),
      tone: 'bg-indigo-500/10 border-indigo-200/50 dark:border-indigo-600/30'
    }
  ];

  const recentDevices = devicesWithStatus.slice(0, 5);

  const getStatusColor = (statusValue: 'online' | 'offline' | 'checking') => {
    switch (statusValue) {
      case 'online':
        return 'bg-emerald-500 ring-emerald-300/50';
      case 'offline':
        return 'bg-rose-500 ring-rose-300/50';
      case 'checking':
        return 'bg-sky-500 ring-sky-300/50';
    }
  };

  const getStatusText = (statusValue: 'online' | 'offline' | 'checking') => {
    switch (statusValue) {
      case 'online':
        return 'Online';
      case 'offline':
        return 'Offline';
      case 'checking':
        return 'Checking...';
    }
  };

  const lastSyncText = status.lastSync ? status.lastSync.toLocaleTimeString() : 'Never';

  const getApiTooltip = () => {
    switch (status.api) {
      case 'online':
        return 'REST API is operational and responding to requests. All device status checks and operations are functioning normally.';
      case 'offline':
        return 'REST API is currently unavailable. Device status checks and remote operations may not work properly.';
      case 'checking':
        return 'Currently testing API connectivity and response times. Please wait for status update.';
    }
  };

  const getDatabaseTooltip = () => {
    switch (status.database) {
      case 'online':
        return 'Database connection is stable and all queries are executing successfully. Device data is being stored and retrieved properly.';
      case 'offline':
        return 'Database connection is down. Device data cannot be saved or retrieved. Please check your connection.';
      case 'checking':
        return 'Testing database connectivity and query performance. Verifying data integrity and access permissions.';
    }
  };

  const getLastSyncTooltip = () => {
    if (!status.lastSync) {
      return 'System status has never been checked. Click refresh to perform initial health check.';
    }
    const timeDiff = Date.now() - status.lastSync.getTime();
    const minutes = Math.floor(timeDiff / 60000);
    if (minutes < 1) {
      return 'System status was just updated. All components are being monitored in real-time.';
    } else if (minutes < 5) {
      return `Last checked ${minutes} minute${minutes > 1 ? 's' : ''} ago. Status is current and reliable.`;
    } else {
      return `Last checked ${minutes} minutes ago. Consider refreshing for the most current status.`;
    }
  };

  return (
    <div className="space-y-6 animate-fade-in premium-grid-bg rounded-3xl p-4 sm:p-7">
      <section className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-5">
        {stats.map((stat) => {
          const Icon = stat.icon;
          return (
            <button
              key={stat.title}
              onClick={stat.onClick}
              className={`glass-panel rounded-2xl p-5 border transition-all duration-300 hover:-translate-y-1 hover:shadow-2xl text-left group motion-lift ${stat.tone}`}
            >
              <div className="mb-4">
                <div className={`w-11 h-11 rounded-xl bg-gradient-to-br ${stat.gradient} flex items-center justify-center shadow-lg shadow-black/20`}>
                  <Icon className="w-6 h-6 text-white" />
                </div>
              </div>

              <div className="space-y-1">
                <p className="text-xs uppercase tracking-[0.12em] font-semibold text-slate-500 dark:text-slate-400">{stat.title}</p>
                <p className={`text-3xl font-black ${stat.textColor} leading-none`}>
                  {stat.value.toLocaleString()}
                </p>
                {stat.title === 'Fleet Overview' && summary.totalDevices > 0 && (
                  <div className="mt-4">
                    <div className="flex justify-between text-[11px] text-slate-600 dark:text-slate-400 mb-2">
                      <span>Unlocked {summary.unlockedInDatabase}</span>
                      <span>Locked {summary.lockedInDatabase}</span>
                    </div>
                    <div className="w-full bg-slate-200 dark:bg-slate-700 rounded-full h-2 overflow-hidden">
                      <div className="h-full flex">
                        <div
                          className="bg-gradient-to-r from-lime-400 to-emerald-500 transition-all duration-700 ease-out"
                          style={{ width: `${safePercent(summary.unlockedInDatabase, summary.totalDevices)}%` }}
                        />
                        <div
                          className="bg-gradient-to-r from-rose-400 to-red-500 transition-all duration-700 ease-out"
                          style={{ width: `${safePercent(summary.lockedInDatabase, summary.totalDevices)}%` }}
                        />
                      </div>
                    </div>
                  </div>
                )}
                {stat.title === 'Reporting Coverage' && summary.totalDevices > 0 && (
                  <div className="mt-4">
                    <div className="flex justify-between text-[11px] text-slate-600 dark:text-slate-400 mb-2">
                      <span>Reported {summary.lockAppRun}</span>
                      <span>No Report {summary.lockAppNotRun}</span>
                    </div>
                    <div className="w-full bg-slate-200 dark:bg-slate-700 rounded-full h-2 overflow-hidden">
                      <div className="h-full flex">
                        <div
                          className="bg-gradient-to-r from-emerald-400 to-teal-500 transition-all duration-700 ease-out"
                          style={{ width: `${safePercent(summary.lockAppRun, summary.totalDevices)}%` }}
                        />
                        <div
                          className="bg-gradient-to-r from-amber-400 to-orange-500 transition-all duration-700 ease-out"
                          style={{ width: `${safePercent(summary.lockAppNotRun, summary.totalDevices)}%` }}
                        />
                      </div>
                    </div>
                  </div>
                )}
                {stat.title === 'Sync Integrity' && summary.totalDevices > 0 && (
                  <div className="mt-4">
                    <div className="flex justify-between text-[11px] text-slate-600 dark:text-slate-400 mb-2">
                      <span>Synced {summary.syncedDevices}</span>
                      <span>Out of Sync {summary.unsyncedDevices}</span>
                    </div>
                    <div className="w-full bg-slate-200 dark:bg-slate-700 rounded-full h-2 overflow-hidden">
                      <div className="h-full flex">
                        <div
                          className="bg-gradient-to-r from-indigo-400 to-blue-500 transition-all duration-700 ease-out"
                          style={{ width: `${safePercent(summary.syncedDevices, summary.totalDevices)}%` }}
                        />
                        <div
                          className="bg-gradient-to-r from-fuchsia-400 to-rose-500 transition-all duration-700 ease-out"
                          style={{ width: `${safePercent(summary.unsyncedDevices, summary.totalDevices)}%` }}
                        />
                      </div>
                    </div>
                  </div>
                )}
                {stat.title === 'Locked by Template' && summary.lockedInDatabase > 0 && (
                  <div className="mt-4 space-y-3">
                    <div className="flex justify-between text-[11px] text-slate-600 dark:text-slate-400 mb-2 gap-3">
                      <span className="truncate">
                        {activeTemplate
                          ? `${truncateLabel(activeTemplate.name, 30)} ${activeTemplate.count}`
                          : 'No template assigned'}
                      </span>
                      <span className="shrink-0">Hover to view</span>
                    </div>
                    <div className="w-full bg-slate-200 dark:bg-slate-700 rounded-full h-2 overflow-hidden flex">
                      {templateBreakdown.map(([templateName, count], index) => (
                        <div
                          key={templateName}
                          className={`h-full bg-gradient-to-r ${templateBarGradients[index % templateBarGradients.length]} transition-all duration-700 ease-out`}
                          style={{ width: `${safePercent(count, summary.lockedInDatabase)}%` }}
                          onMouseEnter={() => setHoveredTemplate({ name: templateName, count })}
                          onMouseLeave={() => setHoveredTemplate(null)}
                          title={`${templateName}: ${count} (${safePercent(count, summary.lockedInDatabase)}%)`}
                        />
                      ))}
                    </div>
                  </div>
                )}
                {stat.title === 'Locked by Template' && summary.lockedInDatabase === 0 && (
                  <div className="mt-4">
                    <div className="text-[11px] text-slate-500 dark:text-slate-400">No locked devices to group</div>
                  </div>
                )}
              </div>
            </button>
          );
        })}
      </section>

      <section className="grid grid-cols-1 xl:grid-cols-3 gap-6">
        <div className="xl:col-span-2 glass-panel rounded-2xl p-6 border border-white/40 dark:border-slate-700/60 shadow-xl">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gradient-to-br from-indigo-500 via-blue-600 to-cyan-500 rounded-xl flex items-center justify-center shadow-lg">
                <TrendingUp className="w-5 h-5 text-white" />
              </div>
              <div>
                <h3 className="text-lg font-black text-slate-900 dark:text-white">Recent Devices</h3>
                <p className="text-sm text-slate-600 dark:text-slate-300">Latest registrations with current lock posture</p>
              </div>
            </div>
            <button
              onClick={() => onNavigateToDevices('all')}
              className="inline-flex items-center gap-1.5 text-sm font-semibold text-sky-700 dark:text-sky-300 hover:text-sky-800 dark:hover:text-sky-200"
            >
              Open devices
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
          
          <div className="space-y-3">
            {recentDevices.length > 0 ? (
              recentDevices.map((device, index) => (
                <button
                  key={device.id}
                  onClick={() => onViewDevice({
                    id: device.id,
                    serial_number: device.serial_number,
                    device_name: device.device_name || '',
                    owner_name: device.owner_name || '',
                    is_locked: device.is_locked,
                    unlock_code: device.unlock_code,
                    note: device.note || '',
                    created_at: device.created_at,
                    updated_at: device.updated_at
                  })}
                  className="w-full flex items-center justify-between p-4 rounded-xl bg-white/60 dark:bg-slate-900/60 border border-white/60 dark:border-slate-700/60 hover:bg-white/80 dark:hover:bg-slate-800/80 transition-all duration-300 group motion-lift"
                  style={{ animationDelay: `${index * 80}ms` }}
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <div className={`w-3 h-3 rounded-full ${device.is_locked ? 'bg-red-500' : 'bg-emerald-500'}`}></div>
                    <div className="text-left min-w-0">
                      <p className="font-semibold text-slate-900 dark:text-white group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors truncate">
                        {device.serial_number}
                      </p>
                      <p className="text-sm text-slate-500 dark:text-slate-300 truncate">
                        {device.device_name || 'No device name'}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="flex flex-col items-end gap-1">
                      <span className={`px-2 py-1 rounded-lg text-xs font-medium ${
                        device.is_locked 
                          ? 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400' 
                          : 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400'
                      }`}>
                        DB: {device.is_locked ? 'Locked' : 'Unlocked'}
                      </span>
                      <span className={`px-2 py-1 rounded-lg text-xs font-medium ${
                        device.lock_app_status.has_run
                          ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400'
                          : 'bg-gray-100 dark:bg-gray-900/30 text-gray-700 dark:text-gray-400'
                      }`}>
                        App: {device.lock_app_status.has_run ? 'Reported' : 'No Report'}
                      </span>
                      {!device.lock_app_status.is_synced && (
                        <span className="px-2 py-1 rounded-lg text-xs font-medium bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-400">
                          Out of Sync
                        </span>
                      )}
                    </div>
                    <Eye className="w-4 h-4 text-slate-400 group-hover:text-blue-500 transition-colors shrink-0" />
                  </div>
                </button>
              ))
            ) : (
              <div className="text-center py-8">
                <div className="w-16 h-16 bg-slate-100/80 dark:bg-slate-700/70 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <Shield className="w-8 h-8 text-slate-400 dark:text-slate-500" />
                </div>
                <h4 className="text-lg font-medium text-slate-900 dark:text-white mb-2">No devices yet</h4>
                <p className="text-slate-500 dark:text-slate-400">Start by registering your first device</p>
              </div>
            )}
          </div>
        </div>

        <div className="glass-panel rounded-2xl p-6 border border-white/40 dark:border-slate-700/60 shadow-xl">
          <div className="flex items-center justify-between mb-5">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-xl flex items-center justify-center shadow-lg">
                <Activity className="w-5 h-5 text-white" />
              </div>
              <div>
                <h3 className="text-lg font-black text-slate-900 dark:text-white">System Signals</h3>
                <p className="text-sm text-slate-600 dark:text-slate-300">Infrastructure and feature controls</p>
              </div>
            </div>
          </div>

          <div className="space-y-3">
            <div className="rounded-xl border border-white/60 dark:border-slate-700/60 bg-white/60 dark:bg-slate-900/70 p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <span className={`w-3 h-3 rounded-full ring-4 ${getStatusColor(status.api)} ${status.api === 'online' ? 'animate-pulse' : ''}`} />
                  <div>
                    <p className="font-semibold text-slate-900 dark:text-white">REST API</p>
                    <p className="text-xs text-slate-500 dark:text-slate-400">{getApiTooltip()}</p>
                  </div>
                </div>
                <span className="text-xs font-semibold px-2.5 py-1 rounded-full bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-200">{getStatusText(status.api)}</span>
              </div>
            </div>

            <div className="rounded-xl border border-white/60 dark:border-slate-700/60 bg-white/60 dark:bg-slate-900/70 p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <span className={`w-3 h-3 rounded-full ring-4 ${getStatusColor(status.database)} ${status.database === 'online' ? 'animate-pulse' : ''}`} />
                  <div>
                    <p className="font-semibold text-slate-900 dark:text-white">Database</p>
                    <p className="text-xs text-slate-500 dark:text-slate-400">{getDatabaseTooltip()}</p>
                  </div>
                </div>
                <span className="text-xs font-semibold px-2.5 py-1 rounded-full bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-200">{getStatusText(status.database)}</span>
              </div>
            </div>

            <div className="rounded-xl border border-white/60 dark:border-slate-700/60 bg-white/60 dark:bg-slate-900/70 p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Clock className="w-4 h-4 text-slate-500 dark:text-slate-400" />
                  <p className="font-semibold text-slate-900 dark:text-white">Last Sync</p>
                </div>
                <span className="text-sm font-semibold text-slate-700 dark:text-slate-200">{lastSyncText}</span>
              </div>
              <p className="mt-2 text-xs text-slate-500 dark:text-slate-400">{getLastSyncTooltip()}</p>
            </div>

            <div className="rounded-xl border border-white/60 dark:border-slate-700/60 bg-white/60 dark:bg-slate-900/70 p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Flag className="w-4 h-4 text-slate-500 dark:text-slate-400" />
                  <p className="font-semibold text-slate-900 dark:text-white">Lock Requests Flag</p>
                </div>
                <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${
                  flagLoading
                    ? 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-300'
                    : showLockRequests
                      ? 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300'
                      : 'bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-200'
                }`}>
                  {flagLoading ? 'Loading' : showLockRequests ? 'Enabled' : 'Disabled'}
                </span>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="glass-panel rounded-2xl p-6 border border-white/40 dark:border-slate-700/60 shadow-xl">
        <div className="grid grid-cols-1 xl:grid-cols-3 gap-5 items-start">
          <div className="rounded-2xl border border-white/60 dark:border-slate-700/60 bg-white/55 dark:bg-slate-900/60 p-5 space-y-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gradient-to-br from-orange-500 to-rose-600 rounded-xl flex items-center justify-center shadow-lg">
                <Activity className="w-5 h-5 text-white" />
              </div>
              <div>
                <h3 className="text-lg font-black text-slate-900 dark:text-white">Lock App Feed</h3>
                <p className="text-sm text-slate-600 dark:text-slate-300">Real-time activity snapshot</p>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3 text-xs">
              <div className="rounded-xl p-3 bg-slate-100/80 dark:bg-slate-800/70">
                <p className="text-slate-500 dark:text-slate-400">Recent Events</p>
                <p className="mt-1 text-xl font-black text-slate-900 dark:text-white">{summary.recentLockActions.length}</p>
              </div>
              <div className="rounded-xl p-3 bg-slate-100/80 dark:bg-slate-800/70">
                <p className="text-slate-500 dark:text-slate-400">Last Sync</p>
                <p className="mt-1 text-sm font-bold text-slate-900 dark:text-white truncate">{lastSyncText}</p>
              </div>
            </div>

            <p className="text-xs text-slate-500 dark:text-slate-400">
              Activity is compacted for faster scanning. Open Devices for full event context and device-level history.
            </p>
          </div>

          <div className="xl:col-span-2 space-y-2">
            {summary.recentLockActions.length > 0 ? (
              summary.recentLockActions.slice(0, 4).map((action, index) => (
                <div
                  key={`${action.serial_number}-${action.timestamp}-${index}`}
                  className="flex items-center justify-between gap-3 p-3.5 bg-white/60 dark:bg-slate-900/70 border border-white/60 dark:border-slate-700/60 rounded-xl"
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <div className={`w-2.5 h-2.5 rounded-full ${
                      action.action === 'locked' ? 'bg-red-500' : 'bg-emerald-500'
                    }`} />
                    <div className="min-w-0">
                      <p className="font-semibold text-slate-900 dark:text-white truncate">{action.serial_number}</p>
                      <p className="text-xs text-slate-500 dark:text-slate-300 truncate">{action.device_name || 'Unknown device'}</p>
                    </div>
                  </div>

                  <div className="flex items-center gap-2 shrink-0">
                    <span className={`px-2 py-1 rounded-lg text-xs font-medium ${
                      action.action === 'locked'
                        ? 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400'
                        : 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400'
                    }`}>
                      {action.action === 'locked' ? 'Locked' : 'Unlocked'}
                    </span>
                    <span className="text-xs text-slate-500 dark:text-slate-400">{new Date(action.timestamp).toLocaleTimeString()}</span>
                  </div>
                </div>
              ))
            ) : (
              <div className="text-center py-8 rounded-2xl border border-white/60 dark:border-slate-700/60 bg-white/55 dark:bg-slate-900/60">
                <div className="w-14 h-14 bg-slate-100/80 dark:bg-slate-700/70 rounded-2xl flex items-center justify-center mx-auto mb-3">
                  <Activity className="w-7 h-7 text-slate-400 dark:text-slate-500" />
                </div>
                <h4 className="text-base font-semibold text-slate-900 dark:text-white mb-1">No recent activity</h4>
                <p className="text-sm text-slate-500 dark:text-slate-400">Lock app reports will appear here</p>
              </div>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}